import {
  Flex,
  PrimaryButton,
  StackScreen,
  useScreenSize,
  useSafeArea,
} from '@banhala/ui-kit';
import React, { Suspense, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import { useHandleBackPressed, useSafeTimeout } from 'shared/src/hooks';
import { PocHeader } from './components/PocHeader';
import { PocEmpty } from './components/PocEmpty';
import { PocInformation } from './components/PocInformation';
import { VerificationModal } from './modals/VerificationModal';
import { AddContactModal } from './modals/AddContactModal';
import { PocRow } from './components/PocRow';
import { usePocList, useDeletePoc } from './queries';
import { useLogViewEvent } from './hooks';
import { useReportTTFD } from 'shared/src/hooks';
import { AnalyticsScreen } from 'shared/src/context';
import type { Poc } from 'shared/src/api/myPageService/getPocList/types';
import { systemAlert } from 'shared/src/component/systemAlert';

const PointOfContact = () => {
  const { width } = useScreenSize();
  const insets = useSafeArea();
  const { showBackButton, onPressBack } = useHandleBackPressed();

  const { data } = usePocList();
  const pocList = data?.poc_list || [];

  const { mutate: deletePoC } = useDeletePoc();

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const setSafeTimeout = useSafeTimeout();

  useReportTTFD();
  useLogViewEvent();

  const handlePressAdd = () => {
    if (pocList.length >= 2) {
      showLimitCountAlert();
      return;
    }
    setShowVerificationModal(true);
  };

  const handleVerificationComplete = () => {
    setShowVerificationModal(false);
    // 인증 완료 후 연락처 추가 화면으로 전환
    setSafeTimeout(() => {
      setShowAddContactModal(true);
    }, 300);
  };

  const confirmDelete = (sno: number) => {
    systemAlert('', '연락처를 삭제하시겠어요?', [
      { text: '취소', style: 'cancel' },
      { text: '삭제', style: 'destructive', onPress: () => deletePoC(sno) },
    ]);
  };

  const showLimitCountAlert = () => {
    systemAlert('', '상담 그룹은 2명까지 가능합니다', [
      { text: '확인', style: 'default' },
    ]);
  };

  return (
    <AnalyticsScreen screen="POINT_OF_CONTACT">
      <StackScreen
        bottom={{
          element: (
            <Flex
              padding={{
                top: 16,
                horizontal: 16,
                bottom: insets.bottom + 16,
              }}
            >
              <PrimaryButton
                variant={'fill'}
                onPress={handlePressAdd}
                color="pink"
                size="xlarge"
              >
                본인 인증하고 추가하기
              </PrimaryButton>
            </Flex>
          ),
        }}
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
      >
        <Suspense
          fallback={
            <Flex flex={1} width={width} margin={{ horizontal: 'auto' }}>
              <Flex.Center flex={1}>
                <ActivityIndicator />
              </Flex.Center>
            </Flex>
          }
        >
          <ScrollView
            contentContainerStyle={{
              width,
              marginHorizontal: 'auto',
            }}
            showsVerticalScrollIndicator={false}
          >
            <Flex padding={{ horizontal: 16 }}>
              <PocHeader />
            </Flex>
            {pocList.length === 0 ? (
              <PocEmpty />
            ) : (
              pocList.map((contact: Poc) => (
                <PocRow
                  key={contact.sno}
                  name={contact.name}
                  relation={contact.relation}
                  mobile={contact.mobile}
                  onDelete={() => {
                    confirmDelete(contact.sno);
                  }}
                />
              ))
            )}
            <Flex margin={{ top: 8 }}>
              <PocInformation />
            </Flex>
          </ScrollView>
        </Suspense>
        <VerificationModal
          visible={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onVerificationComplete={handleVerificationComplete}
        />
        <AddContactModal
          visible={showAddContactModal}
          onClose={() => setShowAddContactModal(false)}
        />
      </StackScreen>
    </AnalyticsScreen>
  );
};

export default PointOfContact;
